/**
 * Error taxonomy — ENGINEERING-PLAN §3.
 * Clients map codes to localized copy; raw messages are never shown to customers.
 */

export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'TOKEN_EXPIRED',
  'NOT_FOUND',
  'CONFLICT_STATUS_TRANSITION',
  'IDEMPOTENT_REPLAY',
  'COUPON_INVALID',
  'COUPON_EXPIRED',
  'COUPON_MIN_ORDER',
  'COUPON_EXHAUSTED',
  'ITEM_UNAVAILABLE',
  'PRICE_CHANGED',
  'PAYMENT_MISMATCH',
  'REFUND_EXCEEDS_CAPTURE',
  'STOCK_INSUFFICIENT',
  'RATE_LIMITED',
  'SPIN_NOT_ELIGIBLE',
  'BRANCH_MISMATCH',
  'INTERNAL',
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

const STATUS_BY_CODE: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  TOKEN_EXPIRED: 401,
  NOT_FOUND: 404,
  CONFLICT_STATUS_TRANSITION: 409,
  IDEMPOTENT_REPLAY: 200,
  COUPON_INVALID: 422,
  COUPON_EXPIRED: 422,
  COUPON_MIN_ORDER: 422,
  COUPON_EXHAUSTED: 422,
  ITEM_UNAVAILABLE: 409,
  PRICE_CHANGED: 409,
  PAYMENT_MISMATCH: 422,
  REFUND_EXCEEDS_CAPTURE: 422,
  STOCK_INSUFFICIENT: 409,
  RATE_LIMITED: 429,
  SPIN_NOT_ELIGIBLE: 409,
  BRANCH_MISMATCH: 403,
  INTERNAL: 500,
}

export class AppError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly details?: unknown

  constructor(code: ErrorCode, message?: string, details?: unknown) {
    super(message ?? code)
    this.name = 'AppError'
    this.code = code
    this.status = STATUS_BY_CODE[code]
    this.details = details
  }

  toJSON() {
    return {
      ok: false as const,
      error: { code: this.code, message: this.message, details: this.details },
    }
  }
}

export function httpStatusFor(code: ErrorCode): number {
  return STATUS_BY_CODE[code]
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError
}
